import { action, extendObservable, computed } from 'mobx';
import { StatusCodes } from 'http-status-codes';

import FanClubService from 'services/FanClubService';
import { translate } from 'services/i18nService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  GET_PERFORMER_FAN_CLUB_INFO,
  GET_PERFORMER_FAN_CLUB_INFO_ERROR,
  GET_PERFORMER_FAN_CLUB_INFO_RESPONSE,
  USER_LOGGED_OUT,
} from 'services/MessagingService/EventNames';
import NotificationService from 'services/NotificationService';
import TrackingService from 'services/TrackingService';
import fanClubStoreInitialState from 'stores/FanClubStoreInitialState';
import UserStore from 'stores/UserStore';
import { FANCLUB_STATUS } from 'enum/profile';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import allUserGroups from 'enum/userGroups';
import parseCatch from 'utils/parseCatch';
import { addPercentage } from 'utils/helpers';


const SUB_CHECK_INTERVAL_LIFETIME_DURATION_SEC = 15;
const SUB_CHECK_INTERVAL_LIFETIME = SUB_CHECK_INTERVAL_LIFETIME_DURATION_SEC * SECOND_AS_MILLISECONDS;
const SUB_CHECK_INTERVAL = 500;

export class FanClubStore {
  constructor() {
    extendObservable(this, fanClubStoreInitialState);
    this.messagingService = messagingService();
    this.messagingService.on(GET_PERFORMER_FAN_CLUB_INFO_RESPONSE, this.handlePerformerFanClubInfoResponse);
    this.messagingService.on(GET_PERFORMER_FAN_CLUB_INFO_ERROR, this.handlePerformerFanClubInfoError);
    this.messagingService.on(USER_LOGGED_OUT, this.handleFullyJoin);
  }

  destructor() {
    this.messagingService.off(GET_PERFORMER_FAN_CLUB_INFO_RESPONSE, this.handlePerformerFanClubInfoResponse);
    this.messagingService.off(GET_PERFORMER_FAN_CLUB_INFO_ERROR, this.handlePerformerFanClubInfoError);
    this.messagingService.off(USER_LOGGED_OUT, this.handleFullyJoin);
  }

  @action getPerformerFanClubInfo = (username) => {
    this.messagingService.emit(GET_PERFORMER_FAN_CLUB_INFO, username);
  };

  handlePerformerFanClubInfoResponse = (res) => {
    this.isFan = res.viewerSubscribed;
    this.performerFanClubInfo = res;
  };

  handlePerformerFanClubInfoError = () => {
    this.isFan = false;
    this.performerFanClubInfo = {};
  };

  handleFullyJoin = () => {
    this.successfullyJoined = false;
  };

  @action joinFanClub = ({ username, accessHash, performerName, tokenPrice }) => {
    this.waitingToJoinFanclub = true;
    FanClubService.subscribeToFanClub(username, accessHash, performerName, tokenPrice)
      .then((response) => {
        if ('data' in response && response.data.status === 'SUCCESS') {
          const subCheck = setInterval(
            () =>
              FanClubService.getPerformerFanClubAuth(performerName).then((res) => {
                if (res.status === StatusCodes.NOT_FOUND) {
                  clearInterval(subCheck);
                }
                if (res.status === StatusCodes.NO_CONTENT) {
                  this.isFan = true;
                  this.successfullyJoined = true;
                  this.waitingToJoinFanclub = false;
                  clearInterval(subCheck);
                  TrackingService.track({
                    event: 'socialEvents',
                    eventCategory: 'fanClub',
                    eventAction: 'joinFanClub',
                  });
                }
              }),
            SUB_CHECK_INTERVAL
          );
          setTimeout(() => clearInterval(subCheck), SUB_CHECK_INTERVAL_LIFETIME);
        }
      })
      .catch((error) => {
        this.successfullyJoined = false;
        this.waitingToJoinFanclub = false;
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('v2profile.notification.error.default'),
          subtype: NotificationService.subtype.ERROR,
        });
        parseCatch(error);
      });
  };

  @action resetFanClubStoreState = () => {
    Object.keys(fanClubStoreInitialState).forEach((item) => (this[item] = fanClubStoreInitialState[item]));
  };

  @computed get isFanClubActive() {
    if (this.performerFanClubInfo) {
      return this.performerFanClubInfo.state === FANCLUB_STATUS.ACTIVATED;
    }

    return false;
  }

  @computed get isFanClubLocked() {
    return this.performerFanClubInfo.state === FANCLUB_STATUS.LOCKED;
  }

  @computed get fanclubPrice() {
    const { user: { tokenUpchargePercentage } } = UserStore;

    const price = this.performerFanClubInfo?.tokenPrice;
    if (!price) {
      return 0;
    }

    if (UserStore.hasCF(allUserGroups.USE_UPCHARGE) && tokenUpchargePercentage) {
      const newPrice = addPercentage(price, tokenUpchargePercentage);

      return newPrice;
    }

    return price;
  }
}
export default new FanClubStore();
